import React, { useState } from 'react';
import '../daily-fee.styles.scss';
import PageLayout from '../../../components/PageLayout';
import useSWR from 'swr';
import {
  formatDate,
  getItemById,
  getQueryString,
  hasExpired,
} from '../../../utils/common.util';
import { useDebounceState } from '../../../utils/useDebounceState';
import Combobox from '../../../components/combobox/Combobox';
import {
  DatePicker,
  DatePickerInput,
  Pagination,
  Select,
  SelectItem,
} from 'carbon-components-react';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { PlusCircle, Printer, ChevronRight } from 'react-feather';
import { PrintView } from '../../../components/PrintView/print-view.component';
import Expandable from '../../../components/expandable/Expandable/Expandable';
import PrintOutHead from '../../../components/PrintOutHead';
import MakeArrearsPayment from './MakeArrearsPayment';
import { useUrlState } from '../../../utils/useUrlState';
import { AdvancePagesList } from './AdvancePagesList';
import ExportReportButton from '../../../components/ExportReportButton';

const initialData = {
  studentId: '',
  academicYearId: '',
  termId: '',
  classId: '',
  dailyBillingFeeId: '',
  genericGroupingId: '',
  dateFrom: '',
  dateTo: '',
};

export default function StudentArrearsPage() {
  const [query, setQuery] = useUrlState(initialData);

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  const { data: d } = useSWR(`daily-billing/fees/list`);
  const feeTypes = d?.dailyBillingFee ?? [];

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: s, isLoading: isStudentsLoading } = useSWR(
    `daily-billing/fees/assigned-students?${getQueryString({
      query: searchStudent,
      dailyBillingFeeId: query.dailyBillingFeeId,
    })}`,
  );
  const students = s?.response ?? [];

  const [paginate, setPaginate] = useState({
    page: 1,
    pageSize: 20,
  });
  const mainUrl = 'daily-billing/fees/student-arrears';
  const {
    data,
    isLoading: isLoadingPayments,
    error,
    mutate: refetchData,
  } = useSWR(
    `${mainUrl}?${getQueryString({
      ...query,
      take: paginate.pageSize,
      skip: paginate.page - 1,
    })}`,
  );
  const studentsData = data?.result ?? [];

  const [isOpen, setIsOpen] = React.useState('');
  const [currentStudentData, setCurrentStudentData] = React.useState(null);

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  function handleClose() {
    setIsOpen('');
    setCurrentStudentData(null);
  }

  // const add_arrears = isAllowed(['DailyBillingFeesArrearsPaymentAccess']);

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: `Student Arrears`, active: true },
      ]}
      title={
        <div className="d-flex align-items-center gap-30">
          <h1 className="h-20">Student Arrears</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn gap-4"
            onClick={() => setIsOpen('arrears')}
          >
            <PlusCircle size="14" strokeWidth="2px" />
            <span>MAKE ARREARS PAYMENT</span>
          </button>
          <AdvancePagesList />
        </div>
      }
      isLoading={isLoadingPayments}
    >
      {/* FILTERS  */}
      {/* <p className="mt-30 text-12">
        Select at least a fee type to show report.
      </p> */}
      <div className="mt-10 card-row-4">
        <Select
          labelText="Fee Type"
          onChange={(e) =>
            setQuery({ dailyBillingFeeId: e.target.value, studentId: '' })
          }
          value={query.dailyBillingFeeId}
        >
          <SelectItem selected value="" text="All" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>
        <Combobox
          options={students ?? []}
          label="Select a Student"
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({ studentId: option ? option.id : '' });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent}
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading}
          renderSelected={(option) => `${option.firstName} ${option.lastName}`}
          renderOption={(option) => `${option.firstName} ${option.lastName}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        />

        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({ academicYearId: e.target.value, termId: '' })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText="Class"
          onChange={(e) => setQuery({ classId: e.target.value })}
          value={query.classId}
        >
          <SelectItem value="" text="All" />
          {classes?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {studentsData?.length === 0 && (
          <p className="text-14 mt-30 text-grey">
            No arrears data for the selected filters.
          </p>
        )}
        {studentsData?.length > 0 && (
          <>
            <PrintOutHead
              title={`Daily Billing Arrears Payments`}
              headings={[
                {
                  label: 'Fee Type',
                  value:
                    getItemById(feeTypes, query.dailyBillingFeeId)?.name ??
                    'All',
                },
                {
                  label: 'Date Range',
                  value: query.dateFrom
                    ? `${formatDate(query.dateFrom)} - ${formatDate(
                        query.dateTo,
                      )} `
                    : '-',
                },
                {
                  label: 'Student',
                  value: query.studentId
                    ? students?.find((s) => s.id === query.studentId)?.fullName
                    : 'All',
                },
                {
                  label: 'Academic Year',
                  value:
                    getItemById(academicYears, query.academicYearId)
                      ?.description ?? 'All',
                },
                {
                  label: 'Term',
                  value: getItemById(terms, query.termId)?.description ?? 'All',
                },
                {
                  label: 'Class',
                  value:
                    getItemById(classes, query.classId)?.description ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.dateFrom
                    ? `${formatDate(query.dateFrom)} - ${formatDate(
                        query.dateTo,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                {studentsData.map((studentData) => (
                  <StudentGroupSection
                    key={`${studentData.student.id}-${studentData.dailyBillingFee.id}`}
                    studentData={studentData}
                    onPay={() => {
                      setCurrentStudentData(studentData);
                      setIsOpen('arrears');
                    }}
                  />
                ))}
              </div>
              <div className="no-print">
                <Pagination
                  backwardText="Previous page"
                  forwardText="Next page"
                  page={paginate.page}
                  pageNumberText="Page Number"
                  pageSize={paginate.pageSize}
                  pageSizes={[10, 20, 30, 40, 50, 100]}
                  totalItems={data?.totalCount || 0}
                  onChange={(value) => setPaginate(value)}
                />
              </div>
            </PrintView>
          </>
        )}
        {studentsData?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`Daily Fee Student Arrears Report.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <MakeArrearsPayment
        isOpen={isOpen === 'arrears'}
        closeModal={handleClose}
        currentStudentData={currentStudentData}
        onSuccess={() => {
          refetchData();
          setCurrentStudentData(null);
        }}
      />
    </PageLayout>
  );
}

const StudentGroupSection = ({ studentData, onPay }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const totals = [
    { label: 'Amount Billed (GH₵)', value: studentData.totalAmountBilled },
    { label: 'Amount Paid (GH₵)', value: studentData.totalAmountPaid },
    { label: 'Arrears (GH₵)', value: studentData.totalBalance },
  ];

  return (
    <div className="mt-30 bg-white p-16 rounded">
      <div
        className="align-items-center"
        style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1fr' }}
      >
        <div className="font-bold flex-col-row">
          <span>
            {studentData.student.firstName} {studentData.student.lastName}
          </span>
          <span>{studentData.student.classDescription}</span>
          <span>{studentData.dailyBillingFee.name}</span>
        </div>
        <div className="card-row-3">
          {totals?.map((total, index) => (
            <div
              key={index}
              className="grid gap-10 text-center no-print"
              style={{
                opacity: isExpanded ? 0 : 1,
                transition: 'opacity 0.3s',
              }}
            >
              <span className="text-10 text-grey">{total.label}</span>
              <span className="font-bold text-18">{total.value}</span>
            </div>
          ))}
        </div>
        <div className="card-row-2" style={{ justifySelf: 'end' }}>
          <button
            onClick={onPay}
            className="clean-btn no-print font-bold bx--btn--ghost"
          >
            Pay
          </button>
          <button
            className="clean-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              size={12}
              strokeWidth={5}
              style={{
                rotate: isExpanded ? '90deg' : '0deg',
                transition: 'rotate 0.3s',
              }}
            />
          </button>
        </div>
      </div>
      <Expandable isExpanded={isExpanded} className="mt-10">
        <table className="bx--data-table table-white">
          <thead>
            <tr>
              {/* <th>Fee Type</th> */}
              <th>Date Created</th>
              <th>Amount Billed (GH₵)</th>
              <th>Amount Paid (GH₵)</th>
              <th>Arrears (GH₵)</th>
              {/* <th className="no-print">Action</th> */}
            </tr>
          </thead>
          <tbody>
            {studentData.unpaidBills.map((bill) => (
              <tr key={bill.id}>
                {/* <td>{bill.feeDescription}</td> */}
                <td>{formatDate(bill.dateCreated)}</td>
                <td>{bill.amountBilled}</td>
                <td>{bill.amountPaid}</td>
                <td>{bill.balanceDue}</td>
                {/* <td></td> */}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-bold">
              <td>Total</td>
              <td>{studentData.totalAmountBilled}</td>
              <td>{studentData.totalAmountPaid}</td>
              <td>{studentData.totalBalance}</td>

              {/* <td className="no-print">
                <button
                  onClick={() => onPay(studentData)}
                  className="bx--btn bx--btn--ghost"
                >
                  Pay
                </button>
              </td> */}
            </tr>
          </tfoot>
        </table>
      </Expandable>
    </div>
  );
};
